import { useMemo, useRef } from 'react'
import { WorkshopSection } from './WorkshopSection'
import { BlockCard } from './BlockCard'
import { workshopCopy } from './workshopCopy'
import { blockSlotsForGroup } from './blockSlots'
import { useFocusedBlock } from './FocusedBlockContext'
import { useComposerStore } from '@/stores/composerStore'
import { usePersonas } from '@/hooks/usePersonas'
import { usePersonaStore } from '@/stores/personaStore'
import { useAuthStore } from '@/stores/authStore'
import { computeTagUniverse } from '@/utils/personaFilters'
import { TagInput } from '@/components/ui'
import type { PersonaBasicsDraft } from './usePersonaBasicsDraft'
import type { ApiPersona } from '@/types/persona'

interface Props { persona: ApiPersona; basics: PersonaBasicsDraft; avatarUrl?: string | null }

export function IdentitySection({ persona, basics, avatarUrl }: Props) {
  const copy = workshopCopy(persona.name)
  const { values, setField } = basics
  const { uploadPersonaAvatar, clearPersonaAvatar } = usePersonas()
  const draft = useComposerStore((s) => s.draftBundle)
  const server = useComposerStore((s) => s.serverBundle)
  const base = draft ?? server
  const slots = blockSlotsForGroup(base, 'who', ['identity'])
  const { open } = useFocusedBlock()
  const fileRef = useRef<HTMLInputElement>(null)
  const name = values.name || persona.name || 'this persona'
  const initial = (values.name || persona.name || '?').charAt(0).toUpperCase()
  // Backend enforces personas:make-public (admin-only in v1). The UI uses
  // the admin role as a proxy for that permission — backend remains authoritative.
  const isAdmin = useAuthStore((s) => s.user?.roles?.includes('admin') ?? false)
  const allPersonas = usePersonaStore((s) => s.personas)
  const tagSuggestions = useMemo(
    () => computeTagUniverse(Object.values(allPersonas)).map((t) => t.tag),
    [allPersonas],
  )

  return (
    <WorkshopSection id="workshop-identity" emoji="👤" title={copy.whoTitle} subtitle="Role, the business represented, and what they know." defaultOpen>
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          {avatarUrl ? (
            <img src={avatarUrl} alt={values.name || persona.name} className="h-14 w-14 rounded-full object-cover" />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-linear-to-br from-brand-accent to-brand-primary text-lg font-semibold text-white">
              {initial}
            </div>
          )}
          <div className="text-sm">
            <label htmlFor="workshop-avatar-input" className="cursor-pointer rounded-md border border-primary/20 px-3 py-1.5 font-medium text-primary">
              Upload photo
            </label>
            <input
              id="workshop-avatar-input"
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) void uploadPersonaAvatar(persona.id, f)
                if (fileRef.current) fileRef.current.value = ''
              }}
            />
            {avatarUrl && (
              <button type="button" className="ml-3 text-primary" onClick={() => void clearPersonaAvatar(persona.id)}>
                Remove
              </button>
            )}
          </div>
        </div>
        <div>
          <label htmlFor="workshop-basics-name" className="block text-sm font-medium">Name</label>
          <input
            id="workshop-basics-name"
            value={values.name}
            onChange={(e) => setField('name', e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label htmlFor="workshop-basics-desc" className="block text-sm font-medium">Short description</label>
          <input
            id="workshop-basics-desc"
            value={values.description}
            onChange={(e) => setField('description', e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Tags</label>
          <TagInput
            value={values.tags}
            onChange={(tags) => setField('tags', tags)}
            suggestions={tagSuggestions}
            placeholder="e.g., favorite, client-x, seasonal"
          />
          <p className="mt-1 text-xs text-muted-foreground">Used to filter personas on the Home and Personas pages.</p>
        </div>
        {(isAdmin || values.isPublic) && (
          <div className="border-t border-border pt-3">
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={values.isPublic}
                disabled={!isAdmin}
                onChange={(e) => setField('isPublic', e.target.checked)}
                className="h-4 w-4 rounded-sm border-border text-primary focus:ring-ring disabled:opacity-50"
              />
              <span className="text-sm font-medium">Public — visible to all users</span>
            </label>
            <p className="ml-7 mt-1 text-xs text-muted-foreground">
              {isAdmin
                ? 'Every authenticated user sees this persona, in addition to its owner and explicit shares.'
                : 'This persona is publicly visible. Only an administrator can change this.'}
            </p>
          </div>
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {slots.map((s) => (
            <BlockCard
              key={`identity:${s.ordinal}`}
              category={s.category}
              index={s.index}
              ordinal={s.ordinal}
              name={name}
              onOpen={() => open({ index: s.index, category: s.category, ordinal: s.ordinal })}
            />
          ))}
        </div>
      </div>
    </WorkshopSection>
  )
}
